import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../../../store/auth';

const API_BASE_URL = 'http://localhost:8787/api/storage';

// Authentication API
export async function loginApi(
  apiToken: string,
  accountId: string,
): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ apiToken, accountId }),
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();

    if (!data.success || !data.accessToken) {
      return false;
    }

    // Store the tokens
    useAuthStore
      .getState()
      .setTokens(data.accessToken, data.refreshToken, data.expiresIn);

    return true;
  } catch (error) {
    console.error('Login error:', error);
    return false;
  }
}

export async function logoutApi(): Promise<boolean> {
  try {
    const { accessToken } = useAuthStore.getState();

    if (!accessToken) {
      useAuthStore.getState().clearTokens();
      return true; // Already logged out
    }

    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    // Clear tokens regardless of response
    useAuthStore.getState().clearTokens();

    return response.ok;
  } catch (error) {
    console.error('Logout error:', error);
    // Still clear token on error
    useAuthStore.getState().clearTokens();
    return false;
  }
}

// Refresh token function
export async function refreshAccessToken(): Promise<boolean> {
  const { refreshToken, setTokens, setRefreshing } = useAuthStore.getState();

  if (!refreshToken) {
    return false;
  }

  try {
    // Set refreshing state to prevent multiple refresh attempts
    setRefreshing(true);

    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    if (!response.ok) {
      // If refresh fails, clear tokens and return false
      useAuthStore.getState().clearTokens();
      return false;
    }

    const data = await response.json();

    if (!data.success || !data.accessToken) {
      useAuthStore.getState().clearTokens();
      return false;
    }

    // Store the new tokens
    setTokens(
      data.accessToken,
      data.refreshToken || refreshToken, // Keep old refresh token if not provided
      data.expiresIn,
    );

    return true;
  } catch (error) {
    console.error('Token refresh error:', error);
    return false;
  } finally {
    setRefreshing(false);
  }
}
